/**
 * Mobile `GET /api/events/[eventId]/search` page size.
 * Matches Network everyone (`NETWORK_EVERYONE_MAX_PAGE_SIZE`) and
 * public sessions-v2 search (`PUBLIC_SESSIONS_V2_MAX_PAGE_SIZE`).
 * Default stays 10, which is what this endpoint already used.
 */
export const GLOBAL_EVENT_SEARCH_DEFAULT_PAGE_SIZE = 10;
export const GLOBAL_EVENT_SEARCH_MAX_PAGE_SIZE = 50;

export function normalizeGlobalEventSearchPagination(
  page?: number,
  limit?: number
) {
  const safePage =
    typeof page === "number" && Number.isFinite(page) && page >= 1
      ? Math.floor(page)
      : 1;
  const requested =
    typeof limit === "number" && Number.isFinite(limit) && limit >= 1
      ? Math.floor(limit)
      : GLOBAL_EVENT_SEARCH_DEFAULT_PAGE_SIZE;

  return {
    page: safePage,
    limit: Math.min(requested, GLOBAL_EVENT_SEARCH_MAX_PAGE_SIZE),
  };
}

/**
 * Home Search sends `filter` as the selected category.
 * Profiles tab: `user`. All tab: `all`. `profile` remains accepted.
 * The Sessions tab does not call this route; it uses sessions-v2.
 * `all` still returns sessions because that tab reads `results.sessions`.
 */
export function globalSearchWantsProfiles(filter: string) {
  return filter === "all" || filter === "profile" || filter === "user";
}

export function globalSearchWantsSessions(filter: string) {
  return filter === "all" || filter === "session";
}

/**
 * Home Search pages with `limit` + `offset` and does not send `page`.
 * An explicit `page` wins so older callers stay on page math.
 * Offset is applied after the limit cap. Readers skip `(page - 1) * limit`,
 * so an offset that is not a multiple of limit rounds down to that page.
 */
export function resolveGlobalSearchWindow(input: {
  page?: number;
  offset?: number;
  limit?: number;
  hasPageParam: boolean;
  hasOffsetParam: boolean;
}) {
  const normalized = normalizeGlobalEventSearchPagination(
    input.page,
    input.limit
  );
  const offset = input.offset;
  if (
    !input.hasPageParam &&
    input.hasOffsetParam &&
    typeof offset === "number" &&
    Number.isFinite(offset) &&
    offset >= 0
  ) {
    const aligned = Math.floor(offset / normalized.limit) * normalized.limit;
    return {
      page: Math.floor(aligned / normalized.limit) + 1,
      limit: normalized.limit,
    };
  }
  return normalized;
}

export type GlobalSearchMeetingTranslation = {
  name?: string | null;
  description?: string | null;
};

/**
 * Case-insensitive substring on any translation name or description.
 * The characters are literal, matching Prisma `contains` (it escapes regex).
 * An empty needle matches nothing so callers can keep the unfiltered list path.
 */
export function meetingTranslationsMatchSearch(
  translations: GlobalSearchMeetingTranslation[],
  search: string
) {
  const needle = search.toLowerCase();
  if (!needle) return false;
  return translations.some((translation) => {
    const name = translation.name?.toLowerCase() ?? "";
    const description = translation.description?.toLowerCase() ?? "";
    return name.includes(needle) || description.includes(needle);
  });
}

export type GlobalSearchMeetingKey = {
  id: string;
  startDate: Date;
  translations: GlobalSearchMeetingTranslation[];
};

/** startDate ascending, then id, so a tied start time does not reshuffle pages. */
export function selectMeetingsForGlobalSearch<T extends GlobalSearchMeetingKey>(
  rows: T[],
  search: string
): T[] {
  if (!search) return [];
  return rows
    .filter((row) => meetingTranslationsMatchSearch(row.translations, search))
    .sort((left, right) => {
      const byDate = left.startDate.getTime() - right.startDate.getTime();
      if (byDate !== 0) return byDate;
      if (left.id < right.id) return -1;
      if (left.id > right.id) return 1;
      return 0;
    });
}

/**
 * Same paging math as `getSessionMeetingPaginated`: the requested page is kept
 * even when it is past the end, and an empty set reports `totalPages` 0.
 */
export function pageGlobalSearchRows<T>(rows: T[], page: number, limit: number) {
  const total = rows.length;
  const totalPages = Math.ceil(total / limit);
  const start = (page - 1) * limit;
  return {
    pageRows: start >= rows.length ? [] : rows.slice(start, start + limit),
    total,
    totalPages,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
    currentPage: page,
  };
}
